import { Clock } from "@tamagui/lucide-icons";
import { useEffect, useState } from "react";
import { XStack, YStack } from "tamagui";

import { ConsentType } from "@/api/generated/models";
import { useUpdateConsents } from "@/api/mutations/consent";
import Nudge from "@/components/Nudge";
import { Paragraph } from "@/components/Typography";
import { SMS_CONSENT_PRIVACY_DISCLOSURE } from "@/constants";
import { useSmsConsentNudge } from "@/hooks/useSmsConsentNudge";
import type { SmsConsentNudgeFlow } from "@/store";

/** Superficial delay before the nudge reveals after it becomes eligible. */
export const NUDGE_REVEAL_DELAY_MS = 2000;

const VIDEO_VISIT_COPY = {
  title: "Your care plan is being finalized...",
  subtitle: "It usually takes up to 10 minutes",
  description:
    "Consent to get visit follow-ups and reminders by text instead of returning here.",
};

const LIVE_CHAT_COPY = {
  title: "Want updates by text?",
  description:
    "Consent to get visit follow-ups and reminders by text, instead of returning here.",
};

const ASYNC_COPY = {
  title: "We can text you with the update!",
  subtitle: "It usually takes 30–60 minutes for review",
  description:
    "To get updates directly via text, instead of a link back here to view, consent below.",
};

const COPY: Record<
  SmsConsentNudgeFlow,
  { title: string; subtitle?: string; description: string }
> = {
  "video-visit": VIDEO_VISIT_COPY,
  "live-chat": LIVE_CHAT_COPY,
  "async-rx": ASYNC_COPY,
  "async-dx": ASYNC_COPY,
  "rx-renewal": ASYNC_COPY,
};

interface SmsConsentNudgeProps {
  conversationId: string;
}

export default function SmsConsentNudge({
  conversationId,
}: SmsConsentNudgeProps) {
  const { shouldShow, flow, dismissNudge } = useSmsConsentNudge(conversationId);
  const { mutate: updateConsents } = useUpdateConsents();

  // Superficial delay so the nudge eases in a couple seconds after it becomes
  // eligible rather than popping in immediately.
  const [hasDelayElapsed, setHasDelayElapsed] = useState(false);
  useEffect(() => {
    if (!shouldShow) return;
    const timer = setTimeout(
      () => setHasDelayElapsed(true),
      NUDGE_REVEAL_DELAY_MS,
    );
    return () => clearTimeout(timer);
  }, [shouldShow]);

  if (!shouldShow || !flow || !hasDelayElapsed) return null;

  const { title, subtitle, description } = COPY[flow];

  const submitConsent = (consented: boolean) => {
    updateConsents(
      {
        data: {
          consents: {
            [ConsentType.sms_consent_to_treat]: consented,
          },
          source: flow,
        },
      },
      {
        onSettled: () => dismissNudge(conversationId),
      },
    );
  };

  return (
    <Nudge
      key="sms-consent"
      title={title}
      onDismiss={() => dismissNudge(conversationId)}
      dismissTrackingId={`sms-consent-nudge-${flow}-dismissed`}
      description={
        <YStack gap="$100">
          {Boolean(subtitle) && (
            <XStack alignItems="center" gap="$50">
              <Clock size="$200" color="$textSecondary" flexShrink={0} />
              <Paragraph size="$sm">{subtitle}</Paragraph>
            </XStack>
          )}
          <Paragraph size="$sm">{description}</Paragraph>
        </YStack>
      }
      footer={
        <Paragraph size="$xs" fontStyle="italic">
          {SMS_CONSENT_PRIVACY_DISCLOSURE}
        </Paragraph>
      }
      primaryAction={{
        label: "I consent",
        onPress: () => submitConsent(true),
        dataTrackingId: `sms-consent-nudge-${flow}-consent`,
      }}
      secondaryAction={{
        label: "I don't consent",
        onPress: () => submitConsent(false),
        dataTrackingId: `sms-consent-nudge-${flow}-decline`,
      }}
    />
  );
}
